import { Check, Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";

const tiers = [
  {
    name: "Minor Service",
    price: "KES 3,500",
    description: "Essential maintenance for daily drivers",
    features: [
      "Engine oil & filter change",
      "Fluid level check & top-up",
      "Tyre pressure & condition check",
      "Basic brake inspection",
      "Battery health test",
    ],
    highlighted: false,
  },
  {
    name: "Standard Service",
    price: "KES 7,500",
    description: "Comprehensive care for reliable performance",
    features: [
      "Everything in Minor Service",
      "Air filter replacement",
      "Spark plug inspection",
      "Brake pad assessment",
      "Suspension check",
      "Full diagnostic scan",
    ],
    highlighted: true,
  },
  {
    name: "Full Engineering Check",
    price: "KES 15,000",
    description: "Deep engineering-level vehicle analysis",
    features: [
      "Everything in Standard Service",
      "150-point inspection report",
      "Compression & leak-down test",
      "Electrical system diagnosis",
      "Transmission fluid service",
      "Detailed written report",
      "Priority scheduling",
    ],
    highlighted: false,
  },
];

const Pricing = () => (
  <>
    <PageHeader title="Service Packages" subtitle="Transparent pricing — all prices include mobile service to your location" breadcrumb="Pricing" />

    <section className="section-padding bg-background relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-primary/3 rounded-full blur-[150px]" />
      
      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 max-w-5xl mx-auto">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-xl p-5 sm:p-7 md:p-8 flex flex-col transition-all duration-500 overflow-hidden ${
                tier.highlighted
                  ? "bg-charcoal-gradient border-2 border-primary/60 shadow-premium scale-[1.01] md:scale-[1.04] hover:scale-[1.02] md:hover:scale-[1.05]"
                  : "bg-card border border-border/50 hover:border-primary/30 hover:shadow-elevated hover:-translate-y-1"
              }`}
            >
              {/* Noise overlay for highlighted */}
              {tier.highlighted && <div className="absolute inset-0 noise-overlay" />}
              
              {tier.highlighted && (
                <div className="relative z-10 flex items-center gap-1.5 mb-4">
                  <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                  <span className="font-heading text-[10px] sm:text-xs uppercase tracking-widest text-primary">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="relative z-10 flex-1 flex flex-col">
                <h3 className={`font-heading text-lg sm:text-xl uppercase ${tier.highlighted ? "text-secondary-foreground" : "text-foreground"}`}>
                  {tier.name}
                </h3>
                <p className={`text-xs sm:text-sm mt-1 mb-5 ${tier.highlighted ? "text-silver/70" : "text-muted-foreground"}`}>{tier.description}</p>
                <p className="font-heading text-3xl sm:text-4xl text-primary mb-1">{tier.price}</p>
                <p className={`text-[10px] uppercase tracking-wider mb-6 ${tier.highlighted ? "text-silver/50" : "text-muted-foreground/60"}`}>per service</p>
                
                <div className="premium-divider mb-6" />
                
                <ul className="space-y-3 flex-1 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className={`flex items-start gap-2.5 text-xs sm:text-sm ${tier.highlighted ? "text-secondary-foreground/80" : "text-muted-foreground"}`}>
                      <div className="p-0.5 rounded-full bg-primary/20 mt-0.5 shrink-0">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={tier.highlighted ? "hero" : "heroOutline"}
                  size="lg"
                  className="w-full"
                  asChild
                >
                  <a href="https://wa.me/254700000000?text=Hi%2C%20I%27d%20like%20to%20book%20a%20service" target="_blank" rel="noopener noreferrer">
                    Book Now <ArrowRight className="h-4 w-4 ml-1" />
                  </a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 sm:mt-16 text-center max-w-xl mx-auto">
          <div className="bg-card border border-border/50 rounded-xl p-6 sm:p-8 shadow-card-premium">
            <p className="font-heading text-lg uppercase text-foreground mb-2">Need Something Custom?</p>
            <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
              Call us for fleet pricing, commercial vehicles, or specialized diagnostics. We'll build a package that fits your needs.
            </p>
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Pricing;
